import React, { useState, useEffect } from "react";
import { 
  FolderOpen, 
  Plus, 
  CheckSquare, 
  Square, 
  Trash2, 
  FileText, 
  Upload, 
  Clock, 
  User, 
  Building, 
  Mail, 
  Phone, 
  FileEdit, 
  CheckCircle, 
  Download, 
  History, 
  ArrowRight,
  RefreshCw,
  Folder
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ephemeralMatterWorkspace } from "@/lib/ephemeralMatterWorkspace";

const FORM_TYPE_TABS = {
  "certification_sheet": "certification",
  "legal_aid_form": "aid-form",
  "apply_for_aid": "apply-aid",
  "intake_form": "intake",
  "authority_to_act": "authority",
  "backsheet": "backsheet",
  "vla_report_worksheet": "medical-worksheet",
  "memo_precedent": "memo-precedent",
  "travel_claim": "travel-claim",
  "billing": "billing",
  "conflict_check": "conflict-check",
  "appeal_cost_certificate": "appeal-cost-certificate",
  "fee_slip_invoice_payment": "fee-slip-invoice-payment"
};

const FORM_LABELS = {
  "certification_sheet": "Guideline Certification",
  "legal_aid_form": "VLA Legal Aid Application",
  "apply_for_aid": "Internal Aid Request",
  "intake_form": "Criminal Intake Details",
  "authority_to_act": "Authority to Act Form",
  "backsheet": "Backsheet to Counsel",
  "vla_report_worksheet": "Medical Report Worksheet",
  "memo_precedent": "Memo Precedent Funding",
  "travel_claim": "Travel Claim Form",
  "billing": "LACW Diary Billing",
  "conflict_check": "Conflict Check Report",
  "appeal_cost_certificate": "Appeal Cost Certificate",
  "fee_slip_invoice_payment": "Fee Slip & Invoice Payment"
};

const BRAND_ALLOWED_FORMS = {
  vals: [
    "certification_sheet",
    "legal_aid_form",
    "apply_for_aid",
    "intake_form",
    "authority_to_act",
    "backsheet",
    "vla_report_worksheet",
    "memo_precedent",
    "fee_slip_invoice_payment",
    "appeal_cost_certificate"
  ],
  lacw: [
    "certification_sheet",
    "legal_aid_form",
    "apply_for_aid",
    "backsheet",
    "vla_report_worksheet",
    "memo_precedent",
    "travel_claim",
    "appeal_cost_certificate",
    "fee_slip_invoice_payment"
  ],
  jbl: [
    "certification_sheet",
    "legal_aid_form",
    "vla_report_worksheet",
    "conflict_check"
  ],
  rga: [
    "certification_sheet",
    "legal_aid_form",
    "apply_for_aid",
    "fee_slip_invoice_payment",
    "appeal_cost_certificate",
    "vla_report_worksheet",
    "memo_precedent"
  ],
  eas: [
    "certification_sheet",
    "legal_aid_form",
    "apply_for_aid"
  ],
  admin: [
    "certification_sheet",
    "legal_aid_form",
    "apply_for_aid",
    "intake_form",
    "authority_to_act",
    "backsheet",
    "vla_report_worksheet",
    "memo_precedent",
    "travel_claim",
    "billing",
    "conflict_check",
    "appeal_cost_certificate",
    "fee_slip_invoice_payment"
  ]
};

export default function MatterWorkspaceView({ onTabChange, brandType = "vals" }) {
  // List of all matters
  const [matters, setMatters] = useState([]);
  const [selectedMatterId, setSelectedMatterId] = useState("");
  const [loadingMatters, setLoadingMatters] = useState(false);

  // Active workspace details
  const [workspaceData, setWorkspaceData] = useState(null);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);

  // New Matter dialog
  const [showNewMatterModal, setShowNewMatterModal] = useState(false);
  const [newMatterId, setNewMatterId] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [creatingMatter, setCreatingMatter] = useState(false);

  // Edit client contact state
  const [editingClient, setEditingClient] = useState(false);
  const [editClientName, setEditClientName] = useState("");
  const [editClientEmail, setEditClientEmail] = useState("");
  const [editClientPhone, setEditClientPhone] = useState("");
  const [editStatus, setEditStatus] = useState("Open");
  const [editNotes, setEditNotes] = useState("");
  const [updatingClient, setUpdatingClient] = useState(false);

  // Add task state
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [addingTask, setAddingTask] = useState(false);

  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Active sub-tab in workspace
  const [workspaceTab, setWorkspaceTab] = useState("overview"); // "overview" | "drafts" | "documents" | "timeline"

  // Load matters list
  const fetchMatters = async () => {
    setLoadingMatters(true);
    try {
      const list = ephemeralMatterWorkspace.list(brandType);
      setMatters(list);
      
      // If we don't have a selected matter but there are matters, select the first one
      if (!selectedMatterId && list.length > 0) {
        setSelectedMatterId(list[0].id);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load client matters.");
    } finally {
      setLoadingMatters(false);
    }
  };

  // Load active workspace details
  const fetchWorkspaceDetails = async (id) => {
    if (!id) return;
    setLoadingWorkspace(true);
    try {
      const data = ephemeralMatterWorkspace.get(brandType, id);
      setWorkspaceData(data);
      
      // Seed edit form states
      setEditClientName(data.matter.client_name || "");
      setEditClientEmail(data.matter.client_email || "");
      setEditClientPhone(data.matter.client_phone || "");
      setEditStatus(data.matter.status || "Open");
      setEditNotes(data.matter.notes || "");
    } catch (err) {
      console.error(err);
      toast.error("Failed to retrieve matter workspace profile.");
    } finally {
      setLoadingWorkspace(false);
    }
  };

  useEffect(() => {
    fetchMatters();
  }, []);

  useEffect(() => {
    if (selectedMatterId) {
      fetchWorkspaceDetails(selectedMatterId);
    } else {
      setWorkspaceData(null);
    }
  }, [selectedMatterId]);

  // Create Matter
  const handleCreateMatter = async (e) => {
    e.preventDefault();
    if (!newMatterId.trim() || !newClientName.trim()) {
      toast.error("Matter Reference and Client Name are required.");
      return;
    }

    setCreatingMatter(true);
    try {
      const payload = {
        id: newMatterId.trim(),
        client_name: newClientName.trim(),
        client_email: newClientEmail.trim(),
        client_phone: newClientPhone.trim(),
        status: "Open",
        notes: newNotes.trim()
      };
      ephemeralMatterWorkspace.create(brandType, payload);
      toast.success(`Matter ${newMatterId} created successfully!`);
      
      // Clear forms
      setNewMatterId("");
      setNewClientName("");
      setNewClientEmail("");
      setNewClientPhone("");
      setNewNotes("");
      setShowNewMatterModal(false);

      // Select new matter and refresh list
      setSelectedMatterId(payload.id);
      fetchMatters();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to create new matter profile.");
    } finally {
      setCreatingMatter(false);
    }
  };

  // Update Client Contact info
  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setUpdatingClient(true);
    try {
      const payload = {
        client_name: editClientName,
        client_email: editClientEmail,
        client_phone: editClientPhone,
        status: editStatus,
        notes: editNotes
      };
      ephemeralMatterWorkspace.update(brandType, selectedMatterId, payload);
      toast.success("Client details updated successfully.");
      setEditingClient(false);
      fetchWorkspaceDetails(selectedMatterId);
      fetchMatters(); // Refresh list to reflect name/status changes
    } catch (err) {
      console.error(err);
      toast.error("Failed to update client details.");
    } finally {
      setUpdatingClient(false);
    }
  };

  // Tasks Checklist Operations
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    setAddingTask(true);
    try {
      ephemeralMatterWorkspace.createTask(brandType, selectedMatterId, newTaskTitle.trim());
      setNewTaskTitle("");
      fetchWorkspaceDetails(selectedMatterId);
      toast.success("Task added to checklist.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to add task.");
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    const nextStatus = currentStatus === "Completed" ? "Pending" : "Completed";
    try {
      ephemeralMatterWorkspace.updateTask(brandType, selectedMatterId, taskId, nextStatus);
      fetchWorkspaceDetails(selectedMatterId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update task status.");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      ephemeralMatterWorkspace.deleteTask(brandType, selectedMatterId, taskId);
      fetchWorkspaceDetails(selectedMatterId);
      toast.success("Task deleted.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete task.");
    }
  };

  // Document Upload Register
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Session documents must be 1.5 MB or smaller.");
      e.target.value = "";
      return;
    }

    setUploadingDoc(true);
    toast.info("Adding document to this private browser session...");

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      try {
        const payload = {
          filename: file.name,
          file_type: file.type || "application/octet-stream",
          category: file.type.includes("pdf") ? "Compliance Document" : "Correspondence",
          storage_path: reader.result,
        };

        ephemeralMatterWorkspace.createDocument(brandType, selectedMatterId, payload);

        toast.success(`Successfully uploaded attachment: ${file.name}`);
        fetchWorkspaceDetails(selectedMatterId);
      } catch (err) {
        console.error(err);
        toast.error("Document upload failed: " + err.message);
      } finally {
        setUploadingDoc(false);
      }
    };
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document from the register?")) return;
    try {
      ephemeralMatterWorkspace.deleteDocument(brandType, selectedMatterId, docId);
      toast.success("Document removed from register.");
      fetchWorkspaceDetails(selectedMatterId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete document: " + err.message);
    }
  };

  // Navigation to load draft into active forms
  const handleEditDraft = (draft) => {
    const tabId = FORM_TYPE_TABS[draft.form_type];
    if (!tabId) {
      toast.error(`Form type ${draft.form_type} is not mapped to a portal tab.`);
      return;
    }
    // Call the parent callback to switch tabs and pass draft ID as parameter
    onTabChange(tabId, draft.id);
  };

  // Create new draft from within workspace
  const handleCreateNewDraft = (formType) => {
    const tabId = FORM_TYPE_TABS[formType];
    if (!tabId) return;
    
    // Switch to target form tab and pass prefill matter_id via URL parameter
    onTabChange(tabId, null, selectedMatterId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "bg-blue-100 text-blue-750 dark:bg-blue-950/40 dark:text-blue-400 border-blue-250";
      case "Under Review": return "bg-amber-100 text-amber-750 dark:bg-amber-950/40 dark:text-amber-400 border-amber-250";
      case "Completed": return "bg-emerald-100 text-emerald-750 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-250";
      default: return "bg-slate-100 text-slate-750 dark:bg-slate-900/40 dark:text-slate-400 border-slate-250";
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Matter Selector Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-6 rounded-2xl shadow-sm mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center text-blue-600">
            <FolderOpen className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-850 dark:text-slate-100">Select Matter Workspace</h2>
            <p className="text-xs text-slate-500 dark:text-slate-450 mt-0.5 font-sans">Private session workspace for matter drafts, documents and tasks</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {loadingMatters ? (
            <RefreshCw className="w-5 h-5 animate-spin text-slate-450" />
          ) : (
            <select
              value={selectedMatterId}
              onChange={(e) => setSelectedMatterId(e.target.value)}
              className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-2 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-700 dark:text-slate-250 w-full md:w-56 cursor-pointer"
            >
              {matters.length === 0 ? (
                <option value="">No Matters Available</option>
              ) : (
                matters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id} - {m.client_name}
                  </option>
                ))
              )}
            </select>
          )}

          <Button
            onClick={() => setShowNewMatterModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold py-2 px-3.5 flex items-center gap-1.5 cursor-pointer shadow-md shadow-blue-500/10"
          >
            <Plus className="w-4 h-4" /> Create Matter
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-900">
        <strong>Session-only privacy:</strong> matter details and attachments stay in this browser session and are cleared when the session ends. Download anything you need to retain in your approved matter system.
      </div>

      {workspaceData ? (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* Left Navigation Workspace Panel */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-2xl shadow-sm p-4 space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 px-3 block mb-2">Matter Index</span>
              
              <button
                onClick={() => setWorkspaceTab("overview")}
                className={`w-full text-left py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer ${
                  workspaceTab === "overview" 
                    ? "bg-blue-50 dark:bg-blue-950/20 text-blue-650 dark:text-blue-450 border border-blue-100/50 dark:border-blue-900/30" 
                    : "text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-950/50 dark:text-slate-350"
                }`}
              >
                <Building className="w-4 h-4" /> Matter Overview
              </button>

              <button
                onClick={() => setWorkspaceTab("drafts")}
                className={`w-full text-left py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer ${
                  workspaceTab === "drafts" 
                    ? "bg-blue-50 dark:bg-blue-950/20 text-blue-650 dark:text-blue-450 border border-blue-100/50 dark:border-blue-900/30" 
                    : "text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-950/50 dark:text-slate-350"
                }`}
              >
                <FileText className="w-4 h-4" /> Workflow Drafts ({workspaceData.drafts.length})
              </button>

              <button
                onClick={() => setWorkspaceTab("documents")}
                className={`w-full text-left py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer ${
                  workspaceTab === "documents" 
                    ? "bg-blue-50 dark:bg-blue-950/20 text-blue-650 dark:text-blue-450 border border-blue-100/50 dark:border-blue-900/30" 
                    : "text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-950/50 dark:text-slate-350"
                }`}
              >
                <Folder className="w-4 h-4" /> Document Register ({workspaceData.documents.length})
              </button>

              <button
                onClick={() => setWorkspaceTab("timeline")}
                className={`w-full text-left py-2.5 px-3 rounded-xl text-xs font-bold flex items-center gap-2.5 transition-all cursor-pointer ${
                  workspaceTab === "timeline" 
                    ? "bg-blue-50 dark:bg-blue-950/20 text-blue-650 dark:text-blue-450 border border-blue-100/50 dark:border-blue-900/30" 
                    : "text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-950/50 dark:text-slate-350"
                }`}
              >
                <Clock className="w-4 h-4" /> History & Timelines ({workspaceData.timeline.length + workspaceData.versions.length})
              </button>
            </div>

            {/* Matter Sidebar Card */}
            <div className="bg-slate-50/50 dark:bg-slate-950/20 border border-slate-150 dark:border-slate-850 p-5 rounded-2xl space-y-4">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block">Matter Reference</span>
                <span className="text-sm font-mono font-bold text-slate-750 dark:text-slate-250 mt-0.5 block">{workspaceData.matter.id}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block">Active Status</span>
                <span className={`px-2 py-0.5 rounded-full border text-[9px] font-bold inline-block mt-1 uppercase ${getStatusColor(workspaceData.matter.status)}`}>
                  {workspaceData.matter.status}
                </span>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block">Client Contact</span>
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-1 block font-sans">{workspaceData.matter.client_name}</span>
              </div>
            </div>
          </div>

          {/* Right Workspace Content Area */}
          <div className="lg:col-span-3 space-y-6">
            {loadingWorkspace ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-2xl p-12 shadow-sm flex items-center justify-center text-slate-400">
                <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading matter folders...
              </div>
            ) : (
              <>
                {/* 1. Overview Tab */}
                {workspaceTab === "overview" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Client Contacts */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-6 rounded-2xl shadow-sm space-y-4">
                      <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-800">
                        <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                          <User className="w-4.5 h-4.5 text-blue-500" /> Client Contact Profile
                        </h3>
                        <Button
                          variant="ghost"
                          onClick={() => setEditingClient(!editingClient)}
                          className="h-7 text-[10px] font-bold uppercase text-slate-450 hover:text-slate-750 flex items-center gap-1 rounded-lg border px-2 border-slate-200/50 cursor-pointer"
                        >
                          <FileEdit className="w-3 h-3" /> {editingClient ? "View Profile" : "Edit Profile"}
                        </Button>
                      </div>

                      {editingClient ? (
                        <form onSubmit={handleUpdateClient} className="space-y-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Full Client Name</label>
                            <Input
                              type="text"
                              required
                              value={editClientName}
                              onChange={(e) => setEditClientName(e.target.value)}
                              className="h-8.5 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-300"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Client Email</label>
                            <Input
                              type="email"
                              value={editClientEmail}
                              onChange={(e) => setEditClientEmail(e.target.value)}
                              className="h-8.5 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-300"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Client Phone</label>
                            <Input
                              type="text"
                              value={editClientPhone}
                              onChange={(e) => setEditClientPhone(e.target.value)}
                              className="h-8.5 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-300"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Matter Status</label>
                            <select
                              value={editStatus}
                              onChange={(e) => setEditStatus(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs h-8.5 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-750 dark:text-slate-250 cursor-pointer font-semibold"
                            >
                              <option value="Open">Open (Initial Intake)</option>
                              <option value="Active">Active (In Progress)</option>
                              <option value="Under Review">Under Review (Audit/Sign-off)</option>
                              <option value="Completed">Completed (Archived)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Internal Matter Notes</label>
                            <textarea
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs p-2.5 h-16 focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-750 dark:text-slate-250 font-sans"
                            />
                          </div>
                          <Button
                            type="submit"
                            disabled={updatingClient}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold py-2 cursor-pointer h-9 shadow shadow-blue-500/10"
                          >
                            {updatingClient ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Save Changes"}
                          </Button>
                        </form>
                      ) : (
                        <div className="space-y-3 font-sans text-xs">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-750 dark:text-slate-350">{workspaceData.matter.client_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-705 dark:text-slate-350 truncate">{workspaceData.matter.client_email || "No email provided"}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-705 dark:text-slate-350">{workspaceData.matter.client_phone || "No phone number"}</span>
                          </div>
                          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-450 block mb-1">Matter Case Notes:</span>
                            <p className="text-slate-500 dark:text-slate-450 leading-relaxed bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-100 dark:border-slate-900 italic text-[11px]">
                              {workspaceData.matter.notes || "No matter-specific notes added yet."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tasks Checklist */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-6 rounded-2xl shadow-sm flex flex-col justify-between h-full">
                      <div className="space-y-4 flex-1">
                        <div className="border-b pb-3 border-slate-100 dark:border-slate-800">
                          <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                            <CheckSquare className="w-4.5 h-4.5 text-blue-500" /> Workspace Task Checklist
                          </h3>
                        </div>

                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          {workspaceData.tasks.length === 0 ? (
                            <p className="text-xs text-slate-450 dark:text-slate-500 italic text-center py-6">Checklist is empty.</p>
                          ) : (
                            workspaceData.tasks.map((task) => (
                              <div key={task.id} className="flex items-center justify-between gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-950/40 rounded-lg transition-colors border border-transparent hover:border-slate-100 dark:hover:border-slate-800 font-sans">
                                <button
                                  onClick={() => handleToggleTask(task.id, task.status)}
                                  className="flex items-center gap-2 text-left flex-1 text-xs cursor-pointer focus:outline-none"
                                >
                                  {task.status === "Completed" ? (
                                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                  ) : (
                                    <Square className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  )}
                                  <span className={`font-semibold truncate ${task.status === "Completed" ? "line-through text-slate-400 font-normal" : "text-slate-700 dark:text-slate-350"}`}>
                                    {task.task_title}
                                  </span>
                                </button>
                                <button
                                  onClick={() => handleDeleteTask(task.id)}
                                  className="text-slate-400 hover:text-rose-500 focus:outline-none p-1 cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <form onSubmit={handleAddTask} className="flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-800 mt-4">
                        <Input
                          type="text"
                          required
                          placeholder="Add action item..."
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          className="h-8.5 text-xs rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 flex-1 text-slate-300"
                        />
                        <Button
                          type="submit"
                          disabled={addingTask}
                          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold px-3 h-8.5 cursor-pointer flex items-center justify-center"
                        >
                          {addingTask ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </Button>
                      </form>
                    </div>
                  </div>
                )}

                {/* 2. Drafts Tab */}
                {workspaceTab === "drafts" && (
                  <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-2xl shadow-sm p-6 space-y-6">
                    <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-800">
                      <div>
                        <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                          <FileText className="w-4.5 h-4.5 text-blue-500" /> Active Workflow Records
                        </h3>
                        <p className="text-[11px] text-slate-500 dark:text-slate-450 mt-0.5 font-sans">Drafts in this browser session linked to this matter ID</p>
                      </div>

                      <div className="relative group">
                        <Button className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 px-3.5 rounded-xl cursor-pointer flex items-center gap-1.5 shadow-md shadow-blue-500/10">
                          <Plus className="w-4 h-4" /> Link New Draft
                        </Button>
                        
                        {/* Dropdown list of forms */}
                        <div className="absolute right-0 top-full mt-1.5 w-56 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl p-1.5 z-40 hidden group-hover:block hover:block font-sans">
                          <span className="text-[9px] font-bold text-slate-400 px-3 py-1.5 block uppercase tracking-wider">Select Form Engine</span>
                          {(BRAND_ALLOWED_FORMS[brandType] || Object.keys(FORM_TYPE_TABS)).map((formKey) => (
                            <button
                              key={formKey}
                              onClick={() => handleCreateNewDraft(formKey)}
                              className="w-full text-left py-1.5 px-3 rounded-lg text-xs hover:bg-slate-50 dark:hover:bg-slate-900 font-semibold text-slate-750 dark:text-slate-350 cursor-pointer"
                            >
                              {FORM_LABELS[formKey]}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {workspaceData.drafts.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 dark:text-slate-500 italic font-sans text-xs">
                          No drafts are currently associated with this matter profile. Click "Link New Draft" to get started.
                        </div>
                      ) : (
                        workspaceData.drafts.map((draft) => (
                          <div key={draft.id} className="flex items-center justify-between py-4 hover:bg-slate-50/30 dark:hover:bg-slate-950/20 px-3 rounded-xl transition-all">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-slate-100 dark:bg-slate-850 border rounded-xl flex items-center justify-center text-slate-500">
                                <FileText className="w-4.5 h-4.5 text-blue-500" />
                              </div>
                              <div className="font-sans text-xs">
                                <h4 className="font-bold text-slate-800 dark:text-slate-200">{draft.draft_name}</h4>
                                <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-450 leading-none">
                                  <span className="bg-slate-100 dark:bg-slate-800 border px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">
                                    {FORM_LABELS[draft.form_type] || draft.form_type}
                                  </span>
                                  <span>•</span>
                                  <span>Saved by {draft.saved_by}</span>
                                  <span>•</span>
                                  <span>{new Date(draft.updated_at).toLocaleDateString("en-AU")}</span>
                                </div>
                              </div>
                            </div>

                            <Button
                              onClick={() => handleEditDraft(draft)}
                              className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-755 text-slate-800 dark:text-slate-200 font-bold text-xs rounded-xl px-4 py-2 cursor-pointer flex items-center gap-1 border border-slate-200 dark:border-slate-700"
                            >
                              Open Worksheet <ArrowRight className="w-3.5 h-3.5 text-blue-500" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* 3. Documents Register Tab */}
                {workspaceTab === "documents" && (
                  <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-2xl shadow-sm p-6 space-y-6">
                    <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-800">
                      <div>
                        <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                          <Folder className="w-4.5 h-4.5 text-blue-500" /> Document Register
                        </h3>
                        <p className="text-[11px] text-slate-500 dark:text-slate-450 mt-0.5 font-sans">Physical files and scans uploaded locally inside matter sub-directory</p>
                      </div>

                      <div className="relative">
                        <input
                          type="file"
                          id="matter-file-upload"
                          className="hidden"
                          onChange={handleDocumentUpload}
                          disabled={uploadingDoc}
                        />
                        <label
                          htmlFor="matter-file-upload"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2.5 px-4 rounded-xl cursor-pointer flex items-center gap-1.5 shadow-md shadow-blue-500/10"
                        >
                          {uploadingDoc ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          Upload Document
                        </label>
                      </div>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                      {workspaceData.documents.length === 0 ? (
                        <div className="text-center py-12 text-slate-400 dark:text-slate-500 italic font-sans text-xs">
                          No session documents attached yet. Browse to attach files.
                        </div>
                      ) : (
                        workspaceData.documents.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between py-4 hover:bg-slate-50/30 dark:hover:bg-slate-950/20 px-3 rounded-xl transition-all">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-slate-100 dark:bg-slate-850 border rounded-xl flex items-center justify-center text-slate-500">
                                <FileText className="w-4.5 h-4.5 text-blue-500" />
                              </div>
                              <div className="font-sans text-xs">
                                <h4 className="font-bold text-slate-800 dark:text-slate-200 truncate max-w-sm">{doc.filename}</h4>
                                <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-450 leading-none">
                                  <span className="bg-slate-100 dark:bg-slate-800 border px-1.5 py-0.5 rounded text-[9px] font-bold uppercase">
                                    {doc.category || "General"}
                                  </span>
                                  <span>•</span>
                                  <span>{doc.file_type || "File"}</span>
                                  <span>•</span>
                                  <span>{doc.detected_date || doc.created_at || "—"}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {doc.storage_path && (
                                <a
                                  href={doc.storage_path}
                                  download={doc.filename}
                                  className="border rounded-xl px-3 py-1.5 text-xs font-bold text-slate-650 hover:bg-slate-50 dark:hover:bg-slate-800 inline-flex items-center gap-1"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </a>
                              )}
                              <Button
                                variant="outline"
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="border border-slate-200 dark:border-slate-800 text-rose-500 hover:bg-rose-50 rounded-xl p-2 h-8.5 w-8.5 cursor-pointer inline-flex items-center justify-center"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* 4. Timeline Tab */}
                {workspaceTab === "timeline" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Activity Feed */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-6 rounded-2xl shadow-sm space-y-4">
                      <div className="border-b pb-3 border-slate-100 dark:border-slate-800">
                        <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                          <Clock className="w-4.5 h-4.5 text-blue-500" /> Matter Activity Timeline
                        </h3>
                      </div>

                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                        {workspaceData.timeline.length === 0 ? (
                          <p className="text-xs text-slate-400 dark:text-slate-500 italic text-center py-12">No recorded activities.</p>
                        ) : (
                          workspaceData.timeline.map((log, idx) => (
                            <div key={idx} className="flex gap-3 text-xs font-sans relative">
                              {idx < workspaceData.timeline.length - 1 && (
                                <div className="absolute left-3.5 top-7 bottom-0 w-0.5 bg-slate-100 dark:bg-slate-800"></div>
                              )}
                              
                              <div className="w-7.5 h-7.5 rounded-full bg-blue-50 dark:bg-blue-950 border flex items-center justify-center flex-shrink-0 text-blue-650 font-bold border-blue-150">
                                {log.action ? log.action[0].toUpperCase() : "A"}
                              </div>
                              <div className="flex-1 mt-0.5">
                                <span className="font-bold text-slate-750 dark:text-slate-250 block">{log.action ? log.action.replace(/_/g, " ").toUpperCase() : "ACTION"}</span>
                                <p className="text-[11px] text-slate-500 dark:text-slate-450 mt-1 leading-normal">{log.details}</p>
                                <div className="flex items-center gap-2 text-[9px] text-slate-400 mt-1.5 font-semibold leading-none">
                                  <span>{log.user_id}</span>
                                  <span>•</span>
                                  <span>{new Date(log.timestamp).toLocaleString("en-AU")}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Version History recovery */}
                    <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 p-6 rounded-2xl shadow-sm space-y-4">
                      <div className="border-b pb-3 border-slate-100 dark:border-slate-800">
                        <h3 className="text-sm font-bold text-slate-850 dark:text-slate-200 flex items-center gap-1.5">
                          <History className="w-4.5 h-4.5 text-blue-500" /> Draft Version History Log
                        </h3>
                      </div>

                      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                        {workspaceData.versions.length === 0 ? (
                          <p className="text-xs text-slate-400 dark:text-slate-500 italic text-center py-12">No draft version checkpoints found.</p>
                        ) : (
                          workspaceData.versions.map((ver, idx) => (
                            <div key={idx} className="flex gap-3 text-xs font-sans relative">
                              {idx < workspaceData.versions.length - 1 && (
                                <div className="absolute left-3.5 top-7 bottom-0 w-0.5 bg-slate-100 dark:bg-slate-800"></div>
                              )}
                              
                              <div className="w-7.5 h-7.5 rounded-full bg-amber-50 dark:bg-amber-950 border border-amber-200 flex items-center justify-center flex-shrink-0 text-amber-650 font-bold">
                                V{ver.version_number}
                              </div>
                              <div className="flex-1 mt-0.5">
                                <span className="font-bold text-slate-750 dark:text-slate-250 block">Version {ver.version_number} Checkpoint</span>
                                <p className="text-[11px] text-slate-500 dark:text-slate-450 mt-1 leading-normal">
                                  Record ID: <code className="bg-slate-100 dark:bg-slate-950 px-1.5 py-0.5 rounded text-[9px] dark:border-slate-800 border">{ver.record_id}</code>
                                </p>
                                <div className="flex items-center gap-2 text-[9px] text-slate-400 mt-1.5 font-semibold leading-none">
                                  <span>Author: {ver.user_email || "System"}</span>
                                  <span>•</span>
                                  <span>{new Date(ver.timestamp).toLocaleString("en-AU")}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-2xl p-16 shadow-sm text-center">
          <FolderOpen className="w-16 h-16 text-slate-350 dark:text-slate-650 mx-auto mb-4 animate-pulse" />
          <h3 className="text-base font-bold text-slate-750 dark:text-slate-200">No active matter profile selected</h3>
          <p className="text-xs text-slate-400 dark:text-slate-550 mt-1 max-w-sm mx-auto leading-relaxed font-sans">
            Please choose an existing client matter from the dropdown above, or click "Create Matter" to bootstrap a new workspace registry.
          </p>
        </div>
      )}

      {/* New Matter Dialog Modal */}
      {showNewMatterModal && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 max-w-md w-full rounded-2xl shadow-2xl p-6 font-sans">
            <h3 className="text-base font-bold text-slate-100 mb-4 flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-500" /> Create Client Matter Workspace
            </h3>

            <form onSubmit={handleCreateMatter} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Matter ID / Reference (unique)
                </label>
                <Input
                  type="text"
                  required
                  placeholder="E.g., 24A12345"
                  value={newMatterId}
                  onChange={(e) => setNewMatterId(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-blue-500 text-slate-250 font-mono"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Client Full Name
                </label>
                <Input
                  type="text"
                  required
                  placeholder="Jane Doe"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-blue-500 text-slate-250"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Client Email
                  </label>
                  <Input
                    type="email"
                    placeholder="jane@doe.com"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                    className="bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-blue-500 text-slate-250"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Client Phone
                  </label>
                  <Input
                    type="text"
                    placeholder="0400 000 000"
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                    className="bg-slate-950 border-slate-800 text-xs rounded-xl focus:border-blue-500 text-slate-250"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Matter description / Notes
                </label>
                <textarea
                  placeholder="Case notes or workflow brief description..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl text-xs p-2.5 h-20 focus:outline-none focus:ring-1 focus:ring-blue-500 text-slate-250 font-sans"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowNewMatterModal(false)}
                  className="flex-1 rounded-xl text-slate-400 hover:text-slate-200 border border-slate-800 text-xs cursor-pointer h-10 font-bold uppercase tracking-wider"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingMatter}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-bold text-xs rounded-xl h-10 cursor-pointer uppercase tracking-wider"
                >
                  {creatingMatter ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Link Matter"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
